import { relations } from 'drizzle-orm'
import { index, pgTable, primaryKey, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { Invoice } from './invoice'
import { Membership } from './membership'

/**
 * Which seat pools one renewal invoice pays for.
 *
 * A school renewing picks pools, not seats — one, another, or several at once — and all of them
 * belong to the same billing cycle, so they share ONE invoice. `Invoice` can only point at a single
 * membership, hence this join.
 *
 * `appliedAt` makes fulfillment idempotent. Extending a pool is a relative move (`endDate` plus the
 * plan's duration), so a retried job would push the window out twice; the fulfiller claims rows by
 * stamping this column and skips whatever is already stamped. Renewal had no idempotency at all
 * before — the old in-place path just wrote a new `endDate` and a retry silently double-extended it.
 */
export const InvoiceMembership = pgTable(
  'invoice_membership',
  {
    invoiceId: uuid()
      .notNull()
      .references(() => Invoice.id, { onDelete: 'cascade' }),
    membershipId: uuid()
      .notNull()
      .references(() => Membership.id, { onDelete: 'cascade' }),
    /** When this pool's window was actually moved. Null until the renewal is fulfilled. */
    appliedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ columns: [t.invoiceId, t.membershipId] }),
    index('idx_invoice_membership_membership').on(t.membershipId),
  ],
)

export const invoiceMembershipRelations = relations(InvoiceMembership, ({ one }) => ({
  invoice: one(Invoice, {
    fields: [InvoiceMembership.invoiceId],
    references: [Invoice.id],
  }),

  membership: one(Membership, {
    fields: [InvoiceMembership.membershipId],
    references: [Membership.id],
  }),
}))

export const InsertInvoiceMembershipSchema = createInsertSchema(InvoiceMembership, {
  invoiceId: z.uuid(),
  membershipId: z.uuid(),
}).omit({ appliedAt: true, createdAt: true })

export const SelectInvoiceMembershipSchema = createSelectSchema(InvoiceMembership, {
  invoiceId: z.uuid(),
  membershipId: z.uuid(),
  appliedAt: z.date().nullable(),
  createdAt: z.date(),
})

export type InsertInvoiceMembership = typeof InvoiceMembership.$inferInsert
export type SelectInvoiceMembership = typeof InvoiceMembership.$inferSelect
